// src/routes/index.js
import React from 'react';
import { Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom';

import LoginPage from '../pages/LoginPage';
import MainLayout from '../layouts/MainLayout';
import ProtectedRoute from './ProtectedRoute';
import GuestRoute from './GuestRoute';

import DashboardPage from '../pages/DashboardPage';
import MyPage from '../pages/MyPage';
import TestManagementPage from '../pages/TestManagementPage';
import CreateTestPage from '../pages/CreateTestPage';
import EditTestPage from '../pages/EditTestPage';
import TakeTestPage from '../pages/TakeTestPage';
import ExamListPage from '../pages/ExamListPage';
import ChapterListPage from '../pages/ChapterListPage';
import ExerciseListPage from '../pages/ExerciseListPage';
import ExercisePage from '../pages/ExercisePage';
import SettingPage from '../pages/SettingPage';

import OnlineUsersPage from '../pages/OnlineUsersPage';
import JournalPracticePage from '../pages/JournalPracticePage';           // ★ trang làm bài journal
import JournalPracticeListPage from '../pages/JournalPracticeListPage';   // ★ trang danh sách journal
import { useAuth } from '../context/AuthContext';

/* ===== 汎用：権限ガード（RBAC） =====
   指定 resource/action を満たさなければホームへ。 */
const RequirePermission = ({ res, action = 'view' }) => {
  const { can } = useAuth();
  const params = useParams();
  const keys = typeof res === 'function' ? (res(params) ?? []) : [res];
  const allowed = (Array.isArray(keys) ? keys : [keys]).some((k) => can(k, action));
  return allowed ? <Outlet /> : <Navigate to="/" replace />;
};

const AppRoutes = () => (
  <Routes>
    {/* ==== Guest (chưa login) ==== */}
    <Route element={<GuestRoute />}>
      <Route path="/login" element={<LoginPage />} />
    </Route>

    {/* ==== Đã đăng nhập ==== */}
    <Route element={<ProtectedRoute />}>
      <Route path="/" element={<MainLayout />}>
        {/* Dashboard */}
        <Route element={<RequirePermission res="dashboard" action="view" />}>
          <Route index element={<DashboardPage />} />
        </Route>

        {/* My Page */}
        <Route element={<RequirePermission res="myPage" action="view" />}>
          <Route path="my-page" element={<MyPage />} />
        </Route>

        {/* Online Users */}
        <Route element={<RequirePermission res="onlineList" action="view" />}>
          <Route path="online" element={<OnlineUsersPage />} />
        </Route>

        {/* Quản lý Test */}
        <Route element={<RequirePermission res="tests" action="view" />}>
          <Route path="tests" element={<TestManagementPage />} />
        </Route>

        {/* Tạo / sửa Test */}
        <Route element={<RequirePermission res="tests" action="edit" />}>
          <Route path="create-test" element={<CreateTestPage />} />
          <Route path="edit-test/:testId" element={<EditTestPage />} />
        </Route>

        {/* Thi thử */}
        <Route element={<RequirePermission res="takeExam" action="view" />}>
          <Route path="exams" element={<ExamListPage />} />
          <Route path="take-test/:testId" element={<TakeTestPage />} />
        </Route>

        {/* 仕訳練習 (Journal practice) – dùng R1_exercises, KHÔNG phụ thuộc level/chapter */}
        <Route element={<RequirePermission res="journalPractise" action="view" />}>
          <Route path="journal-practice" element={<JournalPracticeListPage />} />
          <Route path="journal-practice/:exerciseId" element={<JournalPracticePage />} />
        </Route>

        {/* Level / Chapter / Exercise thường (L1, L2, L3…) */}
        <Route
          element={
            <RequirePermission
              res={({ levelId }) =>
                levelId === 'level1' || levelId === 'level2' || levelId === 'level3'
                  ? levelId
                  : ['level1', 'level2', 'level3']
              }
              action="view"
            />
          }
        >
          {/* Danh sách chapter theo level */}
          <Route path="level/:levelId" element={<ChapterListPage />} />

          {/* Danh sách bài tập thường */}
          <Route path="level/:levelId/chapter/:chapterId" element={<ExerciseListPage />} />

          {/* Trang làm bài tập thường */}
          <Route
            path="level/:levelId/chapter/:chapterId/exercise/:exerciseId"
            element={<ExercisePage />}
          />
        </Route>

        {/* Setting */}
        <Route element={<RequirePermission res="setting" action="view" />}>
          <Route path="setting" element={<SettingPage />} />
        </Route>
      </Route>
    </Route>

    {/* 404 → quay về / */}
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

export default AppRoutes;
